import { FUNDING_METHOD_OPTIONS_MAP } from '@/components/deal/funding-method/funding-method-badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ComboboxPopover } from '@/components/ui/combobox/combobox-popover';
import { cn, pluralize } from '@/lib/utils';
import type { FundingMethod } from '@/models/types';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useFilteredDealsContext } from '../filtered-deals/filtered-deals-provider';
import type { FundingMethodFilters } from '../types';

const FundingMethodFiltersComboBoxPopover = ({
  children,
  comboBoxOnChange,
  comboBoxOpen,
  onOpenChange,
  filters: fundingMethodFilters,
  stayOpenAfterSelection = true,
  alignOffset,
  side = 'bottom',
  align,
  popoverContentClassName,
}: {
  children: ReactNode;
  comboBoxOnChange: (value: string) => void;
  comboBoxOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: FundingMethodFilters;
  stayOpenAfterSelection: boolean;
  alignOffset?: number;
  side?: 'left' | 'top' | 'right' | 'bottom' | undefined;
  align?: 'center' | 'start' | 'end' | undefined;
  popoverContentClassName?: string;
}) => {
  const { getCount } = useFilteredDealsContext();

  const comboBoxOptions = useMemo(() => {
    return FUNDING_METHOD_OPTIONS_MAP.map((option) => {
      const count = getCount('funding_method', option.value);

      return {
        label: (
          <div className="flex flex-row items-center gap-2 text-nowrap">
            {option.icon}
            {option.label}
            <span className="text-xs text-gray-400">
              {count} {pluralize(count, 'deal', 'deals')}
            </span>
          </div>
        ),
        value: option.value,
        icon: (
          <div className="size-4">
            <Checkbox
              checked={fundingMethodFilters[option.value as FundingMethod]}
            />
          </div>
        ),
      };
    });
  }, [fundingMethodFilters, getCount]);

  return (
    <ComboboxPopover
      placeholder={'Status...'}
      options={comboBoxOptions}
      onChange={comboBoxOnChange}
      align={align}
      side={side}
      alignOffset={alignOffset}
      // TODO: Add end adornment and empty message
      endAdornment={undefined}
      emptyMessage={undefined}
      open={comboBoxOpen}
      onOpenChange={onOpenChange}
      stayOpenAfterSelection={stayOpenAfterSelection}
      popoverContentClassName={cn(
        'w-fit min-w-[200px]',
        popoverContentClassName,
      )}
    >
      {children}
    </ComboboxPopover>
  );
};

export default FundingMethodFiltersComboBoxPopover;
